import cloneDeep from 'lodash.clonedeep';
import { action, computed, flow, makeObservable, observable } from 'mobx';
import { defaultInfoCookies, defaultInfoCookiesValidation } from '../constants/infoCookies';
import { infoCookiesDataCast } from '../helpers/infoCookies';
import { IInfoCookies, IInfoCookiesValidation, IResponseInfoCookies } from '../types/infoCookies';
import { appStore } from 'src/mobx/appStore';
import client from 'src/services/client';
import { LoadingStatus } from 'src/types/main';
import { getAbortController, isCanceledRequest } from '~/helpers';
import logger from '~/services/logger';
import { emptyNameValidation } from '~/utils/validation';

class CookiesStore {
  initial: boolean = true;

  constructor() {
    makeObservable(this);
  }

  private getCookiesAbortController = getAbortController();

  @observable private loading: LoadingStatus = LoadingStatus.Init;
  @observable public isEdit: boolean = false;
  @observable public cookies: IInfoCookies = defaultInfoCookies;
  @observable public validation: IInfoCookiesValidation = cloneDeep(defaultInfoCookiesValidation);

  @computed public get isFirstLoading(): boolean {
    return Boolean(this.isLoading && this.initial);
  }

  @computed public get isLoading(): boolean {
    return [LoadingStatus.Loading, LoadingStatus.Refreshing].includes(this.loading);
  }

  @action public init = () => {
    this.loading = LoadingStatus.Init;
    this.isEdit = false;
    this.validation = cloneDeep(defaultInfoCookiesValidation);

    this.getCookies();
  };

  @action public getCookies = flow(function* (this: CookiesStore) {
    this.getCookiesAbortController.abort();
    this.getCookiesAbortController = getAbortController();

    try {
      this.loading = LoadingStatus.Loading;

      const response: IResponseInfoCookies = yield client.get('v4/website-info/cookies', { signal: this.getCookiesAbortController.signal });

      this.initial = false;
      this.cookies = infoCookiesDataCast(response);
      this.validation = cloneDeep(defaultInfoCookiesValidation);

      this.loading = LoadingStatus.Success;
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.loading = LoadingStatus.Success;
        return;
      }

      this.loading = LoadingStatus.Error;
      logger.error({ error, place: 'CookiesStore _getCookies', showModal: true });
    }
  }).bind(this);

  @action public updateCookies = flow(function* (this: CookiesStore) {
    try {
      this.loading = LoadingStatus.Loading;

      yield client.put('v4/website-info/cookies', {
        title: this.cookies?.title,
        text: this.cookies?.message,
        cookies_enabled: this.cookies?.cookiesEnabled,
      });

      yield appStore.fetchSettings();

      this.isEdit = false;
      this.loading = LoadingStatus.Success;

      return true;
    } catch (error: any) {
      this.loading = LoadingStatus.Error;

      if (!this.isErrorResponseHandled(error)) return;
      logger.error({ error, place: 'CookiesStore updateCookies', showModal: true });
    }
  }).bind(this);

  @action public changeCookiesHandler = (value: Partial<IInfoCookies>) => {
    if (!this.cookies) return;

    const key = Object.keys(value)[0];
    const defaultValidation: IInfoCookiesValidation = cloneDeep(defaultInfoCookiesValidation);

    if (key === 'title') {
      if (value.title?.en && this.cookies.title.en !== value.title.en) {
        this.validation.title.enValid = defaultValidation.title.enValid;
      }
      if (value.title?.ar && this.cookies.title.ar !== value.title.ar) {
        this.validation.title.arValid = defaultValidation.title.arValid;
      }
    }

    this.cookies = { ...this.cookies, ...value };
    this.isEdit = true;
  };

  @action private isErrorResponseHandled(error: any): boolean {
    const errorsBag = error.response.data;

    this.validation = cloneDeep(defaultInfoCookiesValidation);

    if (errorsBag['title.en']) {
      this.validation.title.enValid = false;
      this.validation.title.enErrorText = errorsBag['title.en'][0];
      this.validation.isValid = false;
    }
    if (errorsBag['title.ar']) {
      this.validation.title.arValid = false;
      this.validation.title.arErrorText = errorsBag['title.ar'][0];
      this.validation.isValid = false;
    }

    return this.validation.isValid;
  }

  @action public setValidation = (): boolean => {
    this.validation = cloneDeep(defaultInfoCookiesValidation);

    // title validation
    [this.validation.title, this.validation.isValid] = emptyNameValidation(this.cookies.title, this.validation.title, this.validation.isValid);

    return this.validation.isValid;
  };
}

const cookiesStore = new CookiesStore();
export default cookiesStore;
